import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AttachmentMeta,
  EmailMessageDetail,
  EmailMessageSummary,
  InboxPage,
  InboxSearchParams,
  InboxTestResponse
} from '../models/email-inbox.model';

/**
 * Bandeja de entrada del usuario — lectura IMAP en vivo sobre su conexión SMTP propia.
 *
 * Cada request viaja al servidor IMAP del usuario (puede tardar 1-5 s): mostrar loaders.
 * Paginación por CURSOR opaco (no es page Spring): pasar `nextCursor` de la página anterior.
 * El backend resuelve usuario/tenant desde el JWT (endpoints "mine"), no se envía businessId.
 */
@Injectable({ providedIn: 'root' })
export class EmailInboxApiService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}api/email/inbox`;

  list(params: InboxSearchParams): Observable<InboxPage> {
    let httpParams = new HttpParams();
    if (params.q !== undefined) httpParams = httpParams.set('q', params.q);
    if (params.from !== undefined) httpParams = httpParams.set('from', params.from);
    if (params.unreadOnly !== undefined) httpParams = httpParams.set('unreadOnly', params.unreadOnly);
    if (params.since !== undefined) httpParams = httpParams.set('since', params.since);
    if (params.until !== undefined) httpParams = httpParams.set('until', params.until);
    if (params.cursor !== undefined) httpParams = httpParams.set('cursor', params.cursor);
    if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit);
    return this.http.get<InboxPage>(this.apiUrl, { params: httpParams });
  }

  getById(id: string): Observable<EmailMessageDetail> {
    return this.http.get<EmailMessageDetail>(`${this.apiUrl}/${encodeURIComponent(id)}`);
  }

  markRead(id: string, read: boolean): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${encodeURIComponent(id)}/read`, { read });
  }

  /** Mueve el mensaje a la papelera del servidor (no hay borrado definitivo). */
  trash(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${encodeURIComponent(id)}`);
  }

  listAttachments(id: string): Observable<AttachmentMeta[]> {
    return this.http.get<AttachmentMeta[]>(`${this.apiUrl}/${encodeURIComponent(id)}/attachments`);
  }

  /**
   * Descarga binaria. Se observa la respuesta completa para poder leer el filename
   * del header Content-Disposition (con fallback al filename del AttachmentMeta).
   */
  downloadAttachment(id: string, attId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.apiUrl}/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attId)}`,
      { responseType: 'blob', observe: 'response' }
    );
  }

  /** Mensajes del hilo, del más antiguo al más reciente. */
  getThread(threadId: string): Observable<EmailMessageSummary[]> {
    return this.http.get<EmailMessageSummary[]>(`${this.apiUrl}/threads/${encodeURIComponent(threadId)}`);
  }

  /** Prueba la conexión IMAP listando hasta 5 mensajes. */
  testConnection(): Observable<InboxTestResponse> {
    return this.http.post<InboxTestResponse>(`${this.apiUrl}/test`, {});
  }
}
